import React, { useState } from 'react';
import {
    TextField,
    Grid,
    Typography,
    Button,
    Box,
    IconButton,
    InputAdornment
} from '@mui/material';
import { Visibility, VisibilityOff } from '@mui/icons-material';
import { Formik, Form, Field, ErrorMessage } from 'formik';
import * as Yup from 'yup';
import API from '../../utils/Axios.utils';
import { Notification } from '../../utils/Notification';
import { useNavigate } from 'react-router-dom';

// Yup Validation Schema
const passwordValidationSchema = Yup.object({
    newPassword: Yup.string().required('New password is required'),
    confirmPassword: Yup.string()
        .oneOf([Yup.ref('newPassword'), null], 'Passwords must match')
        .required('Confirm password is required'),
});

const ChangePassword = () => {
    const navigate = useNavigate();
    const [newPasswordVisible, setNewPasswordVisible] = useState(false);
    const [confirmPasswordVisible, setConfirmPasswordVisible] = useState(false);
    const [alert, setAlert] = useState({ open: false, message: '', severity: 'success' });

    const handleAlertClose = () => setAlert({ ...alert, open: false });

    const user = JSON.parse(localStorage.getItem('userLoggedIn'));

    return (
        <Formik
            initialValues={{ newPassword: '', confirmPassword: '' }}
            validationSchema={passwordValidationSchema}
            onSubmit={(values, { setSubmitting }) => {
                const payload = {
                    user_id: user?.user_id || '',
                    new_password: values?.newPassword || '',
                    confirm_password: values?.confirmPassword || ''
                }
                const config = {
                    headers: {
                        'Content-Type': 'application/json'
                    }
                };
                API.post(`/api/users/change-password`, payload, config)
                    .then((res) => {
                        setAlert({
                            open: true,
                            message: 'Password changed successfully! Logging out...',
                            severity: 'success'
                        });
                        setTimeout(() => {
                            localStorage.clear();
                            window.location.replace('/login');
                        }, 1500);
                    })
                    .catch((err) => {
                        console.log('Edit error :- ', err);
                        setAlert({
                            open: true,
                            message: err?.response?.data?.message || 'Failed to change password.',
                            severity: 'error'
                        });
                    })
                    .finally(() => setSubmitting(false));
            }}
        >
            {({ touched, errors, isSubmitting }) => (
                <Form>
                    <Notification
                        open={alert.open}
                        message={alert.message}
                        severity={alert.severity}
                        onClose={handleAlertClose}
                    />
                    <Typography variant="subtitle1" fontWeight="bold" mb={2}>
                        Change Password
                    </Typography>
                    <Grid container spacing={3} mb={2}>
                        {/* New Password Field */}
                        <Grid item xs={12} md={6}>
                            <Field
                                as={TextField}
                                name="newPassword"
                                label="New password"
                                type={newPasswordVisible ? 'text' : 'password'}
                                fullWidth
                                variant="outlined"
                                error={touched.newPassword && Boolean(errors.newPassword)}
                                helperText={<ErrorMessage name="newPassword" />}
                                InputProps={{
                                    endAdornment: (
                                        <InputAdornment position="end">
                                            <IconButton
                                                onClick={() => setNewPasswordVisible((prev) => !prev)}
                                                edge="end"
                                            >
                                                {newPasswordVisible ? <VisibilityOff /> : <Visibility />}
                                            </IconButton>
                                        </InputAdornment>
                                    )
                                }}
                            />
                        </Grid>

                        {/* Confirm Password Field */}
                        <Grid item xs={12} md={6}>
                            <Field
                                as={TextField}
                                name="confirmPassword"
                                label="Confirm password"
                                type={confirmPasswordVisible ? 'text' : 'password'}
                                fullWidth
                                variant="outlined"
                                error={touched.confirmPassword && Boolean(errors.confirmPassword)}
                                helperText={<ErrorMessage name="confirmPassword" />}
                                InputProps={{
                                    endAdornment: (
                                        <InputAdornment position="end">
                                            <IconButton
                                                onClick={() => setConfirmPasswordVisible((prev) => !prev)}
                                                edge="end"
                                            >
                                                {confirmPasswordVisible ? <VisibilityOff /> : <Visibility />}
                                            </IconButton>
                                        </InputAdornment>
                                    )
                                }}
                            />
                        </Grid>
                    </Grid>

                    {/* Submit Button */}
                    <Box display="flex" justifyContent="flex-end">
                        <Button
                            type="submit"
                            variant="contained"
                            disabled={isSubmitting}
                            sx={{
                                backgroundColor: '#1976d2',
                                '&:hover': {
                                    backgroundColor: '#1976d2',
                                },
                                mb: 4
                            }}
                        >
                            {isSubmitting ? 'Changing...' : 'Change Password'}
                        </Button>
                    </Box>
                </Form>
            )}
        </Formik>
    );
};

export default ChangePassword;
